/**
 * The map's emoji as SDF icons: the 🚩 over a flagged cell and the vibe after a cell's number.
 *
 * As text they were requests that could only fail. OpenFreeMap serves glyphs below U+10000 and
 * nothing above, for every font, so each emoji's range was a 404 on every map load — and since
 * nearly every cell carries a flag, nearly every map load logged one. MapLibre then drew the emoji
 * itself: TinySDF rasterised it with the browser's emoji font into a signed distance field, and
 * the text shader turned the field into a one-colour silhouette with a halo. That is the look — a
 * red flag, a white flame — and not the emoji's own colours, because a distance field keeps only
 * the outline. Following an emoji with U+FE0F, to make it a cluster MapLibre draws without asking,
 * does not help either: MapLibre asks for a cluster's codepoints as well.
 *
 * So this makes the same field before any layer asks, and hands it to MapLibre as an SDF icon,
 * which the same shader tints and haloes the same way: nothing is requested, nothing looks
 * different. Two ways of doing it that look right and are not, both tried on 2026-10-22:
 *
 *   - The vibe as `['image', …]` inside the label's `format` expression. MapLibre then draws every
 *     label of that tile with `symbol_text_and_icon`, which shows images as plain colour and, with
 *     a halo wider than 1 px, draws each glyph's fill twice: every number in the tile turns
 *     bolder. The vibe is the label's own icon instead, stretched over the number by
 *     `icon-text-fit` so that it lands after it, whatever its width (`labelEmojis`).
 *   - An icon fitted to its text at an `icon-size` other than 1. The fit is worked out in text
 *     pixels, and the size would scale it again. The fields are made for size 1 instead, which is
 *     why their pixel ratio and range follow the text size.
 *
 * The positions come from MapLibre 6.15's text layout (`symbol/shaping.ts`, `symbol/quads.ts`),
 * which is no public API. After a MapLibre upgrade, put a flagged cell and a vibe next to a
 * screenshot from before it; the constants below are where a difference would come from.
 */

/** What `map.addImage` takes for an SDF icon. */
export type EmojiImage = {
  image: { width: number; height: number; data: Uint8Array };
  pixelRatio: number;
};

/** MapLibre's em in layout units. Positions in the layout are in 24ths of an em. */
const ONE_EM = 24;

/**
 * TinySDF as MapLibre runs it for a glyph it draws itself (`GlyphManager._createTinySDF`): a
 * 24 px em at twice the size, 3 px of margin, doubled too. Same numbers, same outline.
 */
const FONT_PX = 2 * ONE_EM;
const BUFFER = 6;
const CUTOFF = 0.25;
const INF = 1e20;

/**
 * How many pixels of distance the field spans, per pixel on screen. The symbol shader puts the
 * outline at 0.75 and a halo `w` pixels out at `0.75 - w / 8`, divided by the font scale, which
 * at `icon-size` 1 is 1 (`symbol_sdf.fragment.glsl`).
 */
const RANGE = 8;

/**
 * Where a glyph MapLibre drew itself stood: its origin on the pen, its baseline 10/24 em below the
 * middle of a one-line label (`SHAPING_DEFAULT_OFFSET`, the glyph border and TinySDF's top
 * adjustment and margin add up to that).
 */
const LOCAL_BASELINE = 10 / ONE_EM;

/** The distances TinySDF works out, and where the emoji stood among them. */
type Field = {
  /** Signed distance to the outline per pixel, outward positive, in pixels of the field. */
  distance: Float64Array;
  width: number;
  height: number;
  /** The pen when the emoji was drawn: at its origin (x), on its baseline (y). */
  x: number;
  y: number;
  /** How far the pen moved on. */
  advance: number;
};

/**
 * The field MapLibre would have made for `emoji` in `fontStack`, which has no emoji and falls
 * back to the system's emoji font as it did there. Null where there is no 2D canvas to draw on.
 */
function fieldOf(emoji: string, fontStack: readonly string[]): Field | null {
  const size = FONT_PX + BUFFER * 4;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (context === null) return null;

  const families = [...fontStack.map((name) => `'${name}'`), 'sans-serif'];
  context.font = `400 ${FONT_PX}px ${families.join(',')}`;
  context.textBaseline = 'alphabetic';
  context.textAlign = 'left';
  context.fillStyle = 'black';

  const metrics = context.measureText(emoji);
  const top = Math.ceil(metrics.actualBoundingBoxAscent);
  const glyphWidth = Math.max(
    0,
    Math.min(
      size - BUFFER,
      Math.ceil(metrics.actualBoundingBoxRight - metrics.actualBoundingBoxLeft),
    ),
  );
  const glyphHeight = Math.min(size - BUFFER, top + Math.ceil(metrics.actualBoundingBoxDescent));
  if (glyphWidth === 0 || glyphHeight <= 0) return null;

  const width = glyphWidth + 2 * BUFFER;
  const height = glyphHeight + 2 * BUFFER;
  context.fillText(emoji, BUFFER, BUFFER + top);
  const coverage = context.getImageData(BUFFER, BUFFER, glyphWidth, glyphHeight).data;

  // Squared distances to the nearest pixel outside the emoji (`outer`) and inside it (`inner`).
  // A pixel the emoji covers only partly counts as an edge that far from its middle.
  const outer = new Float64Array(width * height).fill(INF);
  const inner = new Float64Array(width * height);
  for (let y = 0; y < glyphHeight; y++) {
    for (let x = 0; x < glyphWidth; x++) {
      const a = (coverage[4 * (y * glyphWidth + x) + 3] ?? 0) / 255;
      if (a === 0) continue;
      const j = (y + BUFFER) * width + x + BUFFER;
      const d = 0.5 - a;
      outer[j] = d > 0 ? d * d : 0;
      inner[j] = a === 1 ? INF : d < 0 ? d * d : 0;
    }
  }
  transform(outer, 0, 0, width, height, width);
  transform(inner, BUFFER, BUFFER, glyphWidth, glyphHeight, width);

  const distance = new Float64Array(width * height);
  for (let i = 0; i < distance.length; i++) {
    distance[i] = Math.sqrt(outer[i] ?? 0) - Math.sqrt(inner[i] ?? 0);
  }
  return { distance, width, height, x: BUFFER, y: BUFFER + top, advance: metrics.width };
}

/**
 * Squared Euclidean distance transform, in place, over a `width` × `height` window of a grid
 * `stride` wide: afterwards each cell holds the squared distance to the nearest cell that held
 * 0, starting values counting as squared distances already. One pass down the columns and one
 * along the rows, each the lower envelope of a parabola per cell (Felzenszwalb and Huttenlocher,
 * "Distance Transforms of Sampled Functions", 2012) — which is also what TinySDF does.
 */
function transform(
  grid: Float64Array,
  x0: number,
  y0: number,
  width: number,
  height: number,
  stride: number,
): void {
  const length = Math.max(width, height);
  const f = new Float64Array(length);
  // The parabolas on the envelope (`v`) and where each one hands over to the next (`z`).
  const v = new Uint16Array(length);
  const z = new Float64Array(length + 1);
  const at = (array: Float64Array | Uint16Array, index: number) => array[index] ?? 0;

  const pass = (offset: number, step: number, count: number) => {
    for (let q = 0; q < count; q++) f[q] = at(grid, offset + q * step);
    let k = 0;
    v[0] = 0;
    z[0] = -INF;
    z[1] = INF;
    for (let q = 1; q < count; q++) {
      let s: number;
      do {
        const r = at(v, k);
        s = (at(f, q) - at(f, r) + q * q - r * r) / (q - r) / 2;
      } while (s <= at(z, k) && --k > -1);
      k++;
      v[k] = q;
      z[k] = s;
      z[k + 1] = INF;
    }
    k = 0;
    for (let q = 0; q < count; q++) {
      while (at(z, k + 1) < q) k++;
      const r = at(v, k);
      grid[offset + q * step] = at(f, r) + (q - r) * (q - r);
    }
  };

  for (let x = x0; x < x0 + width; x++) pass(y0 * stride + x, stride, height);
  for (let y = y0; y < y0 + height; y++) pass(y * stride + x0, 1, width);
}

/**
 * The field as an SDF image of `width` × `height`, the field's pen put at (`x`, `y`) in it, for
 * an icon of `icon-size` 1 that looks like `textSize` px text: one field pixel is
 * `textSize / FONT_PX` screen pixels, and RANGE screen pixels of distance span the alpha channel
 * from the outline outward, as TinySDF's do.
 */
function sdfImage(
  field: Field,
  textSize: number,
  box: { width: number; height: number; x: number; y: number },
): EmojiImage {
  const pixelRatio = FONT_PX / textSize;
  const radius = RANGE * pixelRatio;
  // Only the alpha channel is read for an SDF image; outside the field it is 0, far outside.
  const data = new Uint8Array(box.width * box.height * 4).fill(255);
  for (let i = 3; i < data.length; i += 4) data[i] = 0;
  for (let row = 0; row < field.height; row++) {
    for (let column = 0; column < field.width; column++) {
      const d = field.distance[row * field.width + column] ?? INF;
      const j = 4 * ((row + box.y - field.y) * box.width + column + box.x - field.x) + 3;
      data[j] = Math.max(0, Math.min(255, Math.round(255 - 255 * (d / radius + CUTOFF))));
    }
  }
  return { image: { width: box.width, height: box.height, data }, pixelRatio };
}

/**
 * `emoji` as an SDF icon standing where MapLibre drew it as a one-glyph label of `textSize` px
 * moved by `offsetEm` (as `text-offset` would). `offset` goes to `icon-offset` at `icon-size` 1;
 * the paint is the text's, as `icon-color` and `icon-halo-*`.
 */
export function iconEmoji(
  emoji: string,
  fontStack: readonly string[],
  textSize: number,
  offsetEm: readonly [number, number],
): (EmojiImage & { offset: [number, number] }) | null {
  const field = fieldOf(emoji, fontStack);
  if (field === null) return null;
  const made = sdfImage(field, textSize, field);
  // The glyph's origin sat half its advance left of the middle, on the baseline; an icon is
  // placed by its centre, which lies that far from the origin.
  const advance = field.advance / made.pixelRatio;
  return {
    ...made,
    offset: [
      -advance / 2 + offsetEm[0] * textSize + (field.width / 2 - field.x) / made.pixelRatio,
      (LOCAL_BASELINE + offsetEm[1]) * textSize + (field.height / 2 - field.y) / made.pixelRatio,
    ],
  };
}

/** Everything a label needs to show one of `emojis` after its text, as `labelEmojis` makes it. */
export type LabelEmojis = {
  /** Per emoji, to add with `stretchX: [[0, 1]]`. */
  images: Map<string, EmojiImage>;
  /** Per emoji, the label's `text-offset`, in ems. */
  textOffsets: Map<string, [number, number]>;
  /** For every one of them alike: `icon-offset` and `icon-text-fit-padding`, in pixels. */
  iconOffset: [number, number];
  padding: [number, number, number, number];
};

/**
 * `emojis` as icons for a label of `textSize` px whose text ends in a space, kept with U+2060
 * after it: `icon-text-fit: 'width'` stretches each image's first column over the text, and the
 * field after it lands with its pen at the end of the text — where the emoji's glyph stood when
 * it was part of the text. The text moves half the emoji's advance to the left, as it was
 * centred with it. Null where there is no 2D canvas to draw on.
 *
 * The images share one size and one baseline, so that one padding and one offset serve them
 * all: the layer's `icon-text-fit-padding` cannot vary by feature.
 */
export function labelEmojis(
  emojis: readonly string[],
  fontStack: readonly string[],
  textSize: number,
): LabelEmojis | null {
  const fields = new Map<string, Field>();
  for (const emoji of emojis) {
    const field = fieldOf(emoji, fontStack);
    if (field === null) return null;
    fields.set(emoji, field);
  }
  const all = [...fields.values()];
  const above = Math.max(...all.map((field) => field.y));
  const below = Math.max(...all.map((field) => field.height - field.y));
  const width = 1 + Math.max(...all.map((field) => field.width));
  const box = { width, height: above + below, x: 1 + BUFFER, y: above };

  const images = new Map<string, EmojiImage>();
  const textOffsets = new Map<string, [number, number]>();
  for (const [emoji, field] of fields) {
    const made = sdfImage(field, textSize, box);
    images.set(emoji, made);
    textOffsets.set(emoji, [-field.advance / made.pixelRatio / 2 / textSize, 0]);
  }
  const pixelRatio = FONT_PX / textSize;
  return {
    images,
    textOffsets,
    // The icon is centred on the text's line, whose middle is the label's.
    iconOffset: [0, LOCAL_BASELINE * textSize + (box.height / 2 - box.y) / pixelRatio],
    // The fixed part ends the icon: its first BUFFER pixels lie over the end of the text.
    padding: [0, (width - 1 - BUFFER) / pixelRatio, 0, 0],
  };
}
